import React, { MouseEvent, useMemo } from 'react';
import styled from 'styled-components';

import { ReactComponent as ContactPropertyImg } from '../../../assets/images/features/contact-property.svg';
import { ReactComponent as ScheduleTourImg } from '../../../assets/images/features/schedule-a-tour.svg';
import { ReactComponent as PricesAvailImg } from '../../../assets/images/features/prices-avail.svg';
import { ReactComponent as PhotosImg } from '../../../assets/images/features/photos.svg';
import { ReactComponent as NeighborhoodImg } from '../../../assets/images/features/neighborhood.svg';
import { ReactComponent as RequestTextImg } from '../../../assets/images/features/request-text.svg';

import {
  CreateIntegrationButton,
  ServicePlatform,
} from '../../../components/molecules/IntegrationButton';
import {
  IntegrationSerializedResult,
  IntegrationsFactory,
  ServiceKeyWithMore,
} from '../../../../integrations/factory';
import { handleOnKeyUp } from '../../../../shared/keyevents';
import { features } from '../../../../shared/features';
import {
  analyticsEventActions,
  analyticsEventCategory,
} from '../../../utils/constants';
import { Analytics } from '../../../utils';
import { getActiveServices } from '../../../utils/utilities';

const MAX_ICON_COUNT = 2;
const IconSize = 20.7;

const StyledActionBarRow = styled.div<{ $position?: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $position }) =>
    $position === 'left' ? 'flex-start' : 'flex-end'};
  gap: 0.75rem;
  width: 100%;
  padding: 0 0.125rem;
  margin-top: 0.375rem;
`;

const StyledIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 0.0625rem solid #d7d3d0;
  border-radius: 999px;
  background: #ffffff;
  color: #57534e;
  cursor: pointer;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  svg path {
    fill: currentColor;
    transition: fill 0.15s ease;
  }

  &:hover,
  &:focus-visible {
    background: #f5f4f2;
    color: var(--rg-primary-color, #1249a1);
    border-color: #cdddf7;
  }
`;

const StyledMoreButton = styled(StyledIconButton)`
  width: auto;
  min-width: 3.375rem;
  padding: 0 0.875rem;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
`;

type ServiceButtonProps = {
  label: string;
  description: string;
  service: ServiceKeyWithMore;
  integration?: IntegrationSerializedResult;
  eventAction?: string;
  platform: ServicePlatform;
  Icon: React.ElementType | null;
  fallback?: () => void;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: MouseEvent<HTMLButtonElement>) => void;
};

const ServiceButton = ({
  Icon,
  onClick,
  onMouseEnter,
  onMouseLeave,
  eventAction,
}: ServiceButtonProps) => {
  const handleOnClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick?.(event);

    Analytics.event({
      category: analyticsEventCategory.compact,
      action: eventAction,
    });
  };

  if (!Icon) return null;

  return (
    <StyledIconButton
      type="button"
      onClick={handleOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyUp={handleOnKeyUp(handleOnClick)}
      aria-label="Open service"
    >
      <Icon height={IconSize} width={IconSize} />
    </StyledIconButton>
  );
};

const ServiceMoreButton = ({
  label,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ServiceButtonProps) => {
  const handleOnClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <StyledMoreButton
      type="button"
      onClick={handleOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyUp={handleOnKeyUp(handleOnClick)}
    >
      {label}
    </StyledMoreButton>
  );
};

type ListServices = Omit<
  ServiceButtonProps,
  'eventAction' | 'onMouseOver' | 'integration'
>[];

function getFallbackMoreServices(
  services: ListServices,
  integrations: IntegrationSerializedResult
) {
  const moreServiceData = integrations.knock?.services?.more ?? {};

  return services.filter((service) => moreServiceData[service.service]);
}

export function CompactActionBarV2() {
  const { xprops } = window;
  const platform = xprops.screen.isMobile ? 'mobile' : 'desktop';
  const integrations = useMemo(
    () => new IntegrationsFactory(xprops.listing).create(),
    [xprops.listing]
  );
  const hasMore = integrations[platform]?.moreIcon;

  const services: ListServices = [
    {
      service: 'schedule',
      platform,
      label: 'Schedule a Tour',
      fallback: () => xprops.onToggleMessenger(features.SCHEDULE_TOUR, true),
      description:
        'Use the scheduling tool to select a day and time to see this community.',
      Icon: ScheduleTourImg,
    },
    {
      service: 'availability',
      platform,
      label: 'Pricing & Availability',
      description:
        'See pricing and unit availability details for this community.',
      Icon: PricesAvailImg,
    },
    {
      service: 'photos',
      platform,
      label: 'Photo Gallery',
      description: 'View a photo gallery of this community.',
      Icon: PhotosImg,
    },
    {
      service: 'neighborhood',
      platform,
      label: 'Neighborhood',
      description: 'Explore the neighborhood of this community.',
      Icon: NeighborhoodImg,
    },
    {
      service: 'requestText',
      platform,
      label: 'Request SMS Text',
      description:
        'Get in touch with the leasing staff at this community via SMS text.',
      Icon: RequestTextImg,
    },
    {
      service: 'contact',
      platform,
      label: 'Contact Property',
      fallback: () => xprops.onToggleMessenger(features.CONTACT_PROPERTY, true),
      description: 'Get in touch with the leasing staff at this community.',
      Icon: ContactPropertyImg,
    },
  ];

  const platformActiveServices = getActiveServices(services, integrations, platform);
  const activeServices = platformActiveServices.length
    ? platformActiveServices
    : getFallbackMoreServices(services, integrations);
  const IntegrationButton = useMemo(
    () => CreateIntegrationButton<ServiceButtonProps>(ServiceButton),
    []
  );
  const IntegrationMoreButton = useMemo(
    () => CreateIntegrationButton<ServiceButtonProps>(ServiceMoreButton),
    []
  );

  const onMouseEnter = (event: MouseEvent, label: string) => {
    xprops.onTooltipDisplay([event.clientX, event.clientY], label);
  };

  const onMouseLeave = () => {
    xprops.onTooltipDisplay(null);
  };

  const onMoreClick = () => {
    xprops.onToggleMessenger(features.MORE, true);

    Analytics.event({
      action: analyticsEventActions.opens_more_page_via_more_icon,
      category: analyticsEventCategory.compact,
    });
  };

  if (!activeServices.length && !hasMore) return null;

  return (
    <StyledActionBarRow $position={xprops.listing.position}>
      {activeServices.slice(0, MAX_ICON_COUNT).map((service) => (
        <IntegrationButton
          key={service.service}
          service={service.service as ServiceKeyWithMore}
          label={service.label}
          Icon={service.Icon}
          fallback={service.fallback}
          description={service.description}
          platform={
            (platformActiveServices.length ? service.platform : 'more') as ServicePlatform
          }
          onMouseEnter={(e) => onMouseEnter(e, service.label)}
          onMouseLeave={onMouseLeave}
        />
      ))}

      {hasMore ? (
        <IntegrationMoreButton
          service="more"
          platform={platform as ServicePlatform}
          label="More"
          fallback={onMoreClick}
          description=""
          Icon={null}
          onMouseEnter={(e) => onMouseEnter(e, 'More')}
          onMouseLeave={onMouseLeave}
        />
      ) : null}
    </StyledActionBarRow>
  );
}
